import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  ArrowLeft, Star, Heart, ShoppingCart, Truck, Shield, 
  RotateCcw, Package, ChevronLeft, ChevronRight, Minus, Plus,
  Share2, Store, Flag,
  MessageCircle, Eye, TrendingUp
} from 'lucide-react';
import { useCart } from '@/context/CartContext';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/sections/Footer';
import { useAuth } from '@/context/AuthContext';
import ReportListingModal from '@/components/ReportListingModal';
import AuctionSection from '@/components/auction/AuctionSection';
import { ShareButton } from '@/components/ShareModal';
import { Helmet } from 'react-helmet-async';


const API_URL = process.env.REACT_APP_BACKEND_URL;

// Sample reviews data (in production, this would come from API)
const SAMPLE_REVIEWS = [
  { id: 1, user: 'Marcus J.', rating: 5, date: '2025-01-10', comment: 'Excellent quality! Fast delivery and great packaging. Will buy again.', helpful: 12 },
  { id: 2, user: 'Sarah M.', rating: 4, date: '2025-01-08', comment: 'Good product, exactly as described. Shipping took a bit longer than expected.', helpful: 8 },
  { id: 3, user: 'David K.', rating: 5, date: '2025-01-05', comment: 'Love supporting local Jamaican businesses. Product exceeded expectations!', helpful: 15 },
  { id: 4, user: 'Lisa R.', rating: 4, date: '2025-01-02', comment: 'Nice quality for the price. Would recommend to friends.', helpful: 6 },
];

const ProductDetail = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { addToCart, isInCart } = useCart();
  
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const videoEnabled = process.env.REACT_APP_PRODUCT_VIDEO_ENABLED === "true";
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [showReportModal, setShowReportModal] = useState(false);
  const { user, token, isAuthenticated } = useAuth();

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        // Fetch product details
        const res = await fetch(`${API_URL}/api/products/${productId}`);
        if (!res.ok) throw new Error('Product not found');
        const data = await res.json();
        setProduct(data);
        
        // Fetch related products from same category
        const relatedRes = await fetch(`${API_URL}/api/products/by-category/${encodeURIComponent(data.category)}?limit=6`);
        const relatedData = await relatedRes.json();
        // Filter out current product
        setRelatedProducts(relatedData.filter(p => p.id !== productId).slice(0, 4));
        
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProduct();
    window.scrollTo(0, 0);
  }, [productId]);

  // Check like status when product loads
  useEffect(() => {
    const checkLikeStatus = async () => {
      if (!isAuthenticated || !product?.id) return;
      try {
        const res = await api.get(`/products/${product.id}/like-status`);
        setIsWishlisted(res.data.liked);
        setLikesCount(res.data.likes_count || 0);
      } catch (e) {
        console.log("Could not check like status");
      }
    };
    if (product?.id) checkLikeStatus();
  }, [product?.id, isAuthenticated]);

  const handleLike = async () => {
    if (!isAuthenticated) {
      navigate("/login?redirect=" + encodeURIComponent(window.location.pathname));
      return;
    }
    try {
      const res = await api.post(`/products/${product.id}/like`);
      setIsWishlisted(res.data.liked);
      setLikesCount(prev => res.data.liked ? prev + 1 : Math.max(0, prev - 1));
      toast.success(res.data.message);
    } catch (e) {
      toast.error("Could not update wishlist");
    }
  };

  const handleAddToCart = () => {
    if (product) {
      addToCart(product, quantity);
    }
  };

  const handleBuyNow = () => {
    if (product) {
      addToCart(product, quantity);
      navigate('/cart');
    }
  };

  // Calculate discount from either compare_at_price or original_price (seller-set)
  const originalPrice = product?.original_price || product?.compare_at_price;
  const isOnSale = product?.on_sale || (originalPrice && originalPrice > product?.price);
  const discount = isOnSale && originalPrice
    ? Math.round(((originalPrice - product.price) / originalPrice) * 100) 
    : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-3 md:px-4 lg:px-8 py-3 md:py-8 pt-16 md:pt-24">
          <Skeleton className="h-8 w-32 mb-6" />
          <div className="grid lg:grid-cols-2 gap-12">
            <Skeleton className="aspect-square rounded-2xl" />
            <div className="space-y-4">
              <Skeleton className="h-6 w-32" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-6 w-48" />
              <Skeleton className="h-16 w-64" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="container mx-auto px-3 md:px-4 lg:px-8 py-3 md:py-8 pt-16 md:pt-24 text-center">
          <h1 className="font-display text-4xl text-foreground mb-4">Product Not Found</h1>
          <p className="text-muted-foreground mb-6">The product you're looking for doesn't exist or has been removed.</p>
          <Button variant="jamaica" onClick={() => navigate('/#shop')}>
            <ArrowLeft className="w-4 h-4" />
            Back to Shop
          </Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Dynamic OG Meta Tags for Social Sharing */}
      <Helmet>
        <title>{product.title} | Liantiao Jamaica</title>
        <meta name="description" content={product.description?.substring(0, 160) || "Shop quality products from Jamaican sellers"} />
        <meta property="og:type" content="product" />
        <meta property="og:url" content={`https://liantiaoja.com/product/${product.id}`} />
        <meta property="og:title" content={`${product.title} | Liantiao Jamaica`} />
        <meta property="og:description" content={product.description?.substring(0, 160) || "Shop quality products from Jamaican sellers"} />
        <meta property="og:image" content={product.images?.[0] || "https://liantiaoja.com/icons/icon-512x512.png"} />
        <meta property="og:site_name" content="Liantiao Jamaica" />
        <meta property="product:price:amount" content={product.price?.toString() || "0"} />
        <meta property="product:price:currency" content="JMD" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${product.title} | Liantiao Jamaica`} />
        <meta name="twitter:description" content={product.description?.substring(0, 160) || "Shop quality products from Jamaican sellers"} />
        <meta name="twitter:image" content={product.images?.[0] || "https://liantiaoja.com/icons/icon-512x512.png"} />
      </Helmet>
      <Navbar />
      
      <main className="container mx-auto px-3 md:px-4 lg:px-8 py-3 md:py-8 pt-16 md:pt-24">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground mb-2 md:mb-6 flex-wrap">
          <Link to="/" className="hover:text-primary">Home</Link>
          <span>/</span>
          <Link to="/#shop" className="hover:text-primary">Shop</Link>
          <span>/</span>
          <Link to={`/#shop`} className="hover:text-primary">{product.category}</Link>
          <span>/</span>
          <span className="text-foreground">{product.title}</span>
        </nav>

        {/* Back Button */}
        <Button variant="ghost" className="mb-6" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>

        {/* Product Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-6 lg:gap-12 mb-8 md:mb-16">
          {/* Product Images */}
          <div className="space-y-4">
            {/* Product Video */}
            {videoEnabled && product.videos?.length > 0 && (
              <div className="relative aspect-video rounded-2xl overflow-hidden bg-black mb-4">
                <video
                  controls
                  playsInline
                  preload="metadata"
                  className="w-full h-full object-contain"
                  poster={product.images?.[0]}
                >
                  <source src={`${process.env.REACT_APP_BACKEND_URL}${product.videos[0].url}`} type="video/mp4" />
                </video>
              </div>
            )}
            
            <div className="relative aspect-[4/3] md:aspect-square rounded-xl md:rounded-2xl overflow-hidden bg-muted">
              <img 
                src={product.images?.[selectedImage] || 'https://via.placeholder.com/600'} 
                alt={product.title}
                className="w-full h-full object-cover"
              />
              {discount && (
                <Badge className="absolute top-4 left-4 bg-destructive text-white text-lg px-4 py-1">
                  -{discount}% OFF
                </Badge>
              )}
              {/* Share and Wishlist buttons */}
              <div className="absolute top-4 right-4 flex gap-2">
                <ShareButton product={product} variant="ghost" size="icon" className="w-12 h-12 rounded-full bg-blue-600 text-white hover:bg-blue-700 shadow-lg border-2 border-white" />
              <button 
                onClick={handleLike}
                className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                  isWishlisted ? 'bg-red-500 text-white' : 'bg-pink-500 text-white hover:bg-pink-600'
                }`}
              >
                <Heart className={`w-6 h-6 ${isWishlisted ? 'fill-current' : ''}`} />
              </button>
              
              </div>
              {/* Image navigation */}
              {product.images?.length > 1 && (
                <>
                  <button 
                    onClick={() => setSelectedImage(prev => prev > 0 ? prev - 1 : product.images.length - 1)}
                    className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 shadow-lg border-2 border-white z-10"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button 
                    onClick={() => setSelectedImage(prev => prev < product.images.length - 1 ? prev + 1 : 0)}
                    className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 rounded-full bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 shadow-lg border-2 border-white z-10"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}
            </div>
            
            {/* Thumbnail images */}
            {product.images?.length > 1 && (
              <div className="flex gap-3">
                {product.images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImage(index)}
                    className={`w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${
                      selectedImage === index ? 'border-primary' : 'border-transparent hover:border-primary/50'
                    }`}
                  >
                    <img src={img} alt={`${product.title} ${index + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div>
            {/* Seller */}
            <Link to="#" className="inline-flex items-center gap-2 text-primary hover:underline mb-2">
              <Store className="w-4 h-4" />
              {product.seller_name}
            </Link>
            
            {/* Title */}
            <h1 className="font-display text-lg md:text-3xl lg:text-4xl text-foreground mb-1 md:mb-4">{product.title}</h1>
            
            {/* Rating */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex items-center">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className={`w-5 h-5 ${i < Math.floor(product.rating || 0) ? 'fill-secondary text-secondary' : 'text-muted'}`} />
                ))}
              </div>
              <span className="font-semibold">{product.rating || 0}</span>
              <span className="text-muted-foreground">({product.review_count || 0} reviews)</span>
              <Badge variant="secondary">{product.category}</Badge>
            </div>

            {/* Price */}
            <div className="flex items-baseline gap-4 mb-6">
              <span className="font-display text-2xl md:text-5xl text-primary">${product.price?.toLocaleString()}</span>
              {originalPrice && originalPrice > product.price && (
                <span className="text-2xl text-muted-foreground line-through">${originalPrice?.toLocaleString()}</span>
              )}
              {discount && discount > 0 && (
                <Badge className="bg-green-600 text-white">Save ${(originalPrice - product.price).toLocaleString()}</Badge>
              )}
            </div>
            
            {/* Free Shipping Badge */}
            {product.free_shipping && (
              <div className="flex items-center gap-2 mb-4 bg-green-500/10 text-green-600 px-4 py-2 rounded-lg">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span className="font-medium">Free Shipping on this item!</span>
              </div>
            )}

            {/* Description */}
            <p className="text-muted-foreground mb-6 leading-relaxed">
              {product.description || 'High-quality product from local Jamaican sellers. Perfect for those who appreciate authentic Caribbean goods.'}
            </p>

            {/* Stock Status */}
            <div className="flex items-center gap-2 mb-6">
              <Package className="w-5 h-5 text-primary" />
              <span className={`font-medium ${product.stock_quantity > 10 ? 'text-primary' : product.stock_quantity > 0 ? 'text-secondary' : 'text-destructive'}`}>
                {product.stock_quantity > 10 ? 'In Stock' : product.stock_quantity > 0 ? `Only ${product.stock_quantity} left` : 'Out of Stock'}
              </span>
            </div>

            {/* Views & Sales Social Proof - Always Visible */}
            <div className="flex flex-wrap items-center gap-2 md:gap-4 mb-2 md:mb-4 p-2 md:p-3 bg-slate-800/50 rounded-lg border border-slate-700">
              <span className="flex items-center gap-2 text-sm text-yellow-400 font-medium">
                <Eye className="w-5 h-5" />
                {product.views_count || product.views_24h || 0} people viewed this
              </span>
              {(product.sold_this_month > 0 || product.sales_count > 0) && (
                <span className="flex items-center gap-2 text-sm text-emerald-400 font-medium">
                  <TrendingUp className="w-5 h-5" />
                  {product.sold_this_month || product.sales_count} sold this month
                </span>
              )}
            </div>

            {/* Quantity Selector */}
            <div className="flex items-center gap-4 mb-6">
              <span className="font-medium">Quantity:</span>
              <div className="flex items-center border-2 border-border rounded-lg">
                <button 
                  onClick={() => setQuantity(q => Math.max(1, q - 1))}
                  className="p-3 hover:bg-muted transition-colors"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="px-6 font-semibold">{quantity}</span>
                <button 
                  onClick={() => setQuantity(q => q + 1)}
                  className="p-3 hover:bg-muted transition-colors"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2 mb-3 md:mb-8">
              <Button 
                variant="jamaica" 
                size="default" 
                className="flex-1 min-w-[100px] h-9 md:h-12 text-sm md:text-base"
                onClick={handleAddToCart}
                data-testid="add-to-cart-btn"
              >
                <ShoppingCart className="w-5 h-5" />
                {isInCart(product.id) ? 'Add More' : 'Add to Cart'}
              </Button>
              <Button 
                variant="gold" 
                size="default" 
                className="flex-1 min-w-[80px] h-9 md:h-12 text-sm md:text-base"
                onClick={handleBuyNow}
                data-testid="buy-now-btn"
              >
                Buy Now
              </Button>
              <ShareButton product={product} variant="outline" size="default" className="px-6 bg-blue-600 text-white hover:bg-blue-700 border-blue-600" />
              <Button 
                variant="outline" 
                size="default" 
                className="px-6 bg-orange-500 text-white hover:bg-orange-600 border-orange-500"
                onClick={() => isAuthenticated ? setShowReportModal(true) : navigate('/login?redirect=' + encodeURIComponent(window.location.pathname))}
                title="Report this listing"
                data-testid="report-listing-btn"
              >
                <Flag className="w-5 h-5" />
              </Button>
              <Button 
                variant="outline" 
                size="default" 
                className="px-4 md:px-6 bg-green-600 text-white hover:bg-green-700 border-green-600"
                onClick={() => {
                  if (!isAuthenticated) {
                    navigate('/login?redirect=' + encodeURIComponent(window.location.pathname));
                    return;
                  }
                  // Navigate to messages with this product and seller
                  navigate('/messages?product=' + product.id + '&seller=' + (product.business_id || product.seller_id));
                }}
                title="Contact Seller"
                data-testid="contact-seller-btn"
              >
                <MessageCircle className="w-4 h-4 md:w-5 md:h-5" />
                <span className="hidden md:inline ml-1">Ask Seller</span>
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="grid grid-cols-3 gap-4 p-4 bg-muted rounded-xl">
              <div className="flex flex-col items-center text-center">
                <Truck className="w-6 h-6 text-primary mb-2" />
                <span className="text-xs font-medium">Free Delivery on Selected Items</span>
                <span className="text-xs text-muted-foreground">On selected items</span>
              </div>
              <div className="flex flex-col items-center text-center">
                <Shield className="w-6 h-6 text-primary mb-2" />
                <span className="text-xs font-medium">Secure Payment</span>
                <span className="text-xs text-muted-foreground">100% Protected</span>
              </div>
              <div className="flex flex-col items-center text-center">
                <RotateCcw className="w-6 h-6 text-primary mb-2" />
                <span className="text-xs font-medium">Easy Returns</span>
                <span className="text-xs text-muted-foreground">7 Day Policy</span>
              </div>
            </div>
            
            {/* Auction Section - Shows if product has auction enabled */}
            {product.auction_enabled && (
              <div className="mt-6">
                <AuctionSection product={product} onBidPlaced={() => fetchProduct()} />
              </div>
            )}
          </div>
        </div>

        {/* Tabs Section */}
        <Tabs defaultValue="reviews" className="mb-16">
          <TabsList className="w-full justify-start border-b rounded-none bg-transparent p-0 mb-6">
            <TabsTrigger value="reviews" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3">
              Reviews ({product.review_count || SAMPLE_REVIEWS.length})
            </TabsTrigger>
            <TabsTrigger value="details" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3">
              Product Details
            </TabsTrigger>
            <TabsTrigger value="shipping" className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent px-6 py-3">
              Shipping & Returns
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="reviews" className="mt-0">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Rating Summary */}
              <div className="bg-muted rounded-xl p-6">
                <div className="text-center mb-6">
                  <div className="font-display text-6xl text-primary mb-2">{product.rating || 4.5}</div>
                  <div className="flex justify-center mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`w-6 h-6 ${i < Math.floor(product.rating || 4.5) ? 'fill-secondary text-secondary' : 'text-muted-foreground'}`} />
                    ))}
                  </div>
                  <p className="text-muted-foreground">Based on {product.review_count || SAMPLE_REVIEWS.length} reviews</p>
                </div>
                <Button variant="jamaica" className="w-full">Write a Review</Button>
              </div>
              
              {/* Reviews List */}
              <div className="lg:col-span-2 space-y-6">
                {SAMPLE_REVIEWS.map(review => (
                  <div key={review.id} className="border-b border-border pb-6 last:border-0">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                          {review.user.charAt(0)}
                        </div>
                        <div>
                          <p className="font-semibold">{review.user}</p>
                          <p className="text-xs text-muted-foreground">{review.date}</p>
                        </div>
                      </div>
                      <div className="flex">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className={`w-4 h-4 ${i < review.rating ? 'fill-secondary text-secondary' : 'text-muted'}`} />
                        ))}
                      </div>
                    </div>
                    <p className="text-muted-foreground mb-3">{review.comment}</p>
                    <button className="text-sm text-primary hover:underline">
                      Helpful ({review.helpful})
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="details">
            <div className="prose max-w-none">
              <h3>Product Description</h3>
              <p>{product.description || 'High-quality product sourced from local Jamaican suppliers. We take pride in offering authentic Caribbean goods that support local communities and businesses.'}</p>
              <h3>Specifications</h3>
              <ul>
                <li><strong>Category:</strong> {product.category}</li>
                <li><strong>SKU:</strong> {product.sku || product.id?.slice(0, 8).toUpperCase()}</li>
                <li><strong>Seller:</strong> {product.seller_name}</li>
                <li><strong>Stock:</strong> {product.stock_quantity || 'Available'} units</li>
              </ul>
            </div>
          </TabsContent>
          
          <TabsContent value="shipping">
            <div className="prose max-w-none">
              <h3>Shipping Information</h3>
              <ul>
                <li><strong>Free Delivery:</strong> On selected items</li>
                <li><strong>Standard Shipping:</strong> 3-5 business days</li>
                <li><strong>Express Shipping:</strong> 1-2 business days (additional fee)</li>
                <li><strong>Island-wide Coverage:</strong> We deliver to all parishes in Jamaica</li>
              </ul>
              <h3>Return Policy</h3>
              <ul>
                <li>7-day return window from delivery date</li>
                <li>Items must be unused and in original packaging</li>
                <li>Contact seller for return authorization</li>
                <li>Refunds processed within 5-7 business days</li>
              </ul>
            </div>
          </TabsContent>
        </Tabs>

        {/* Related Products */}
        {relatedProducts.length > 0 && (
          <section className="mb-16">
            <h2 className="font-display text-3xl text-foreground mb-6">Related Products</h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map(relatedProduct => (
                <Link key={relatedProduct.id} to={`/product/${relatedProduct.id}`}>
                  <Card className="group border-border/50 hover:border-primary/30 hover:shadow-xl transition-all duration-300 hover:-translate-y-2 overflow-hidden">
                    <div className="relative aspect-square overflow-hidden bg-muted">
                      <img 
                        src={relatedProduct.images?.[0] || 'https://via.placeholder.com/400'} 
                        alt={relatedProduct.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      {relatedProduct.compare_at_price && (
                        <Badge className="absolute top-3 left-3 bg-destructive text-white">
                          -{Math.round(((relatedProduct.compare_at_price - relatedProduct.price) / relatedProduct.compare_at_price) * 100)}%
                        </Badge>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <p className="text-xs text-muted-foreground mb-1">{relatedProduct.seller_name}</p>
                      <h4 className="font-semibold text-foreground text-sm line-clamp-2 mb-2 group-hover:text-primary transition-colors">
                        {relatedProduct.title}
                      </h4>
                      <div className="flex items-center gap-1 mb-2">
                        <Star className="w-4 h-4 fill-secondary text-secondary" />
                        <span className="text-sm">{relatedProduct.rating}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-display text-lg text-primary">${relatedProduct.price?.toLocaleString()}</span>
                        {relatedProduct.compare_at_price && (
                          <span className="text-xs text-muted-foreground line-through">${relatedProduct.compare_at_price?.toLocaleString()}</span>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Report Listing Modal */}
        <ReportListingModal
          productId={product?.id}
          productTitle={product?.title}
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          token={token}
        />
      </main>
      
      <Footer />
    </div>
  );
};

export default ProductDetail;
